import React, { useState, useEffect } from 'react';
import { RefreshCw, FileText, Search, Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Card, LoadingState, EmptyState, Modal, Input } from '@/components/common';
import { SearchableSelect } from '../common/SearchableSelect';
import {
  getContractTypes,
  createContractType,
  updateContractType,
  deleteContractType,
  type ContractType,
} from '@/services/nomenclatoare.service';

export function ContractTypesPage() {
  const [items, setItems] = useState<ContractType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContractType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ code: '', name: '', sort_order: '', is_active: true });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [itemToDelete, setItemToDelete] = useState<ContractType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadItems = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getContractTypes();
      setItems(data);
    } catch (err) {
      console.error('Failed to load contract types:', err);
      setError('Eroare la incarcarea tipurilor de contracte');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const filtered = items.filter(item => {
    const q = searchQuery.toLowerCase();
    return item.name.toLowerCase().includes(q) || (item.code || '').toLowerCase().includes(q);
  });

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ code: '', name: '', sort_order: '', is_active: true });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (item: ContractType) => {
    setEditingItem(item);
    setFormData({ code: item.code || '', name: item.name, sort_order: String(item.sort_order || ''), is_active: item.is_active });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({ code: '', name: '', sort_order: '', is_active: true });
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) errors.code = 'Codul este obligatoriu';
    if (!formData.name.trim()) errors.name = 'Numele este obligatoriu';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      if (editingItem) {
        await updateContractType(editingItem.id, {
          code: formData.code,
          name: formData.name,
          sort_order: formData.sort_order ? parseInt(formData.sort_order) : undefined,
          is_active: formData.is_active,
        });
        toast.success('Tip contract actualizat!');
      } else {
        await createContractType({
          code: formData.code,
          name: formData.name,
          sort_order: formData.sort_order ? parseInt(formData.sort_order) : undefined,
        });
        toast.success('Tip contract creat!');
      }
      closeModal();
      loadItems();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Eroare la salvare');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteContractType(itemToDelete.id);
      toast.success('Tip contract sters!');
      setItemToDelete(null);
      loadItems();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Eroare la stergere');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleActive = async (item: ContractType) => {
    try {
      await updateContractType(item.id, { is_active: !item.is_active });
      loadItems();
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  if (isLoading) {
    return <LoadingState message="Se incarca..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-teal-500 text-white">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tipuri contracte</h1>
            <p className="text-gray-600 mt-1">Nomenclator tipuri de contracte (Servicii, Lucrări, Furnizare etc.)</p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filtered.length} tipuri
          </span>
          <Button variant="secondary" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={loadItems}>
            Reincarca
          </Button>
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal}>
            Adauga tip contract
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">{error}</div>
      )}

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cauta..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState icon="list" title="Nu exista tipuri de contracte" description={searchQuery ? 'Nu s-au gasit rezultate.' : 'Adaugati primul tip de contract.'} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ordine</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cod</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nume</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map(item => (
                  <tr key={item.id} className={`hover:bg-gray-50 ${!item.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-gray-300" />
                        {item.sort_order}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-600">{item.code || '-'}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => toggleActive(item)}>
                        {item.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Activ</span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Inactiv</span>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" leftIcon={<Edit2 className="w-4 h-4" />} onClick={() => openEditModal(item)}>
                          Editeaza
                        </Button>
                        <Button variant="ghost" size="sm" leftIcon={<Trash2 className="w-4 h-4" />} onClick={() => setItemToDelete(item)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          Sterge
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingItem ? 'Editeaza tip contract' : 'Adauga tip contract'} size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={closeModal} disabled={isSaving}>Anuleaza</Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={isSaving}>
              {editingItem ? 'Salveaza' : 'Creeaza'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Cod" type="text" placeholder="Ex: servicii" value={formData.code}
            onChange={e => setFormData({ ...formData, code: e.target.value })} error={formErrors.code} required />
          <Input label="Nume" type="text" placeholder="Ex: Contract servicii" value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })} error={formErrors.name} required />
          <Input label="Nr. ordine" type="number" placeholder="Ex: 1" value={formData.sort_order}
            onChange={e => setFormData({ ...formData, sort_order: e.target.value })} />
          {editingItem && (
            <div>
              <SearchableSelect
                label="Status"
                value={formData.is_active ? 'true' : 'false'}
                onChange={v => setFormData({ ...formData, is_active: v === 'true' })}
                options={[
                  { value: 'true', label: 'Activ' },
                  { value: 'false', label: 'Inactiv' },
                ]}
              />
            </div>
          )}
        </form>
      </Modal>

      <Modal isOpen={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Confirmare stergere" size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setItemToDelete(null)} disabled={isDeleting}>Anuleaza</Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting} leftIcon={<Trash2 className="w-4 h-4" />}>Sterge</Button>
          </div>
        }
      >
        <p className="text-gray-600">Sigur doriti sa stergeti <strong>"{itemToDelete?.name}"</strong>?</p>
        <p className="text-sm text-red-600 mt-2">Aceasta actiune nu poate fi anulata.</p>
      </Modal>
    </div>
  );
}
